import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { CenteredSpinner } from '../../../../components/ui/Spinner';
import { firebaseAuth } from '../../../../lib/firebase';
import { listErrorMessage } from '../../../../lib/portalAssets/gridView';
import { assetsListHref, operationalAccounts, uploadAccount } from '../../../../lib/portalAssets/pageState';
import { AssetUploader } from '../../../portalAssets';
import { fetchPortalMe } from '../portalApi';
import { PortalShell } from '../PortalShell';
import { portalAssetsApi } from './portalAssetsApi';

// /portal/assets/upload — the upload screen (spec §4.5).
//
// ⚠️ With several locations the club CHOOSES which one, every time; the page
// never defaults to the first (see pageState.uploadAccount).

export function PortalAssetsUploadPage() {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['portal-me'], queryFn: fetchPortalMe, retry: false });
  const options = me.data ? operationalAccounts(me.data.accounts) : [];
  const accountId = uploadAccount(options, params.get('account'));
  const permitted = me.data ? me.data.permissions.includes('assets') : null;

  return (
    <PortalShell signedInAs={firebaseAuth.currentUser?.email ?? null} nav>
      <Link to={assetsListHref({ account: accountId ?? undefined })} className="text-sm text-muted-foreground underline">
        ← All assets
      </Link>
      <h1 className="mt-4 text-2xl font-semibold tracking-tight text-foreground">Upload photos and videos</h1>
      <p className="mt-2 max-w-prose text-sm text-muted-foreground">
        Keep this page open until your uploads finish. Kyck reviews each file before it is used.
      </p>
      <div className="mt-6">
        {me.isPending ? (
          <CenteredSpinner />
        ) : !permitted ? (
          <p className="max-w-md text-sm text-muted-foreground">{listErrorMessage(403).message}</p>
        ) : options.length === 0 ? (
          <p className="max-w-md text-sm text-muted-foreground">Your login isn't linked to a location you can upload to.</p>
        ) : (
          <div className="space-y-5">
            {options.length > 1 && (
              <label className="block max-w-xl text-sm text-foreground">
                <span className="mb-1 block font-medium">Location</span>
                <select
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground"
                  value={accountId ?? ''}
                  onChange={(e) => {
                    const next = new URLSearchParams(params);
                    if (e.target.value) next.set('account', e.target.value);
                    else next.delete('account');
                    setParams(next, { replace: true });
                  }}
                >
                  <option value="">Choose a location…</option>
                  {options.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <AssetUploader
              audience="club"
              api={portalAssetsApi}
              accountId={accountId}
              noAccountReason="Choose the location these photos and videos are from."
              onFinished={() => void qc.invalidateQueries({ queryKey: ['portal-assets'] })}
            />
          </div>
        )}
      </div>
    </PortalShell>
  );
}
